import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STATUSES } from "@/lib/constants";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import { X, ExternalLink, Pencil, Trash2, History, Loader2 } from "lucide-react";

export default function TaskDetailDrawer({ taskId, onClose, onChanged, onEdit }) {
  const { isAdmin } = useAuth();
  const [task, setTask] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  const load = () => taskId && api.get(`/tasks/${taskId}`).then(r => setTask(r.data));

  useEffect(() => { setTask(null); setLogs([]); setShowLogs(false); load(); }, [taskId]);

  if (!taskId) return null;

  const loadLogs = () => {
    setShowLogs(true);
    api.get(`/tasks/${taskId}/activity`).then(r => setLogs(r.data));
  };

  const setStatus = async (status) => {
    setSavingStatus(true);
    try {
      await api.patch(`/tasks/${taskId}`, { status });
      await load();
      onChanged && onChanged();
    } finally { setSavingStatus(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this task? This cannot be undone.")) return;
    await api.delete(`/tasks/${taskId}`);
    onChanged && onChanged();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-40 bg-gray-900/30 backdrop-blur-sm" onClick={onClose}>
      <aside
        data-testid="task-detail-drawer"
        className="fixed inset-y-0 right-0 w-full max-w-[640px] bg-white shadow-2xl border-l border-gray-200 flex flex-col animate-slide-in-right"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gray-500">Task details</div>
          <button onClick={onClose} data-testid="drawer-close-btn" className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!task ? (
          <div className="p-6 text-sm text-gray-400">Loading…</div>
        ) : (
          <div className="overflow-y-auto flex-1 p-6 space-y-6">
            {/* Header */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <PriorityBadge priority={task.priority} />
                <StatusBadge status={task.status} />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight text-gray-900">{task.title}</h2>
              <div className="text-sm text-gray-500">
                {task.client_name} · {task.project} · {task.work_type}
              </div>
            </div>

            {/* Status changer */}
            <div className="bg-gray-50/60 border border-gray-200 rounded-lg p-4">
              <div className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-2">
                Update status {savingStatus && <Loader2 className="w-3 h-3 animate-spin" />}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {STATUSES.map(s => (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    disabled={s === task.status || savingStatus}
                    data-testid={`status-switch-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${s === task.status ? "border-navy bg-navy text-white" : "border-gray-300 bg-white hover:border-navy hover:text-navy"}`}
                  >{s}</button>
                ))}
              </div>
            </div>

            {/* Fields */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <Detail label="Assignee" value={task.assignee_name} />
              <Detail label="Due date" value={task.due_date || "—"} />
              <Detail label="Format" value={task.format || "—"} />
              <Detail label="Audience" value={task.audience || "—"} />
              <Detail label="Episode no." value={task.episode_no ?? "—"} />
              <Detail label="Created by" value={task.creator_name} />
              <Detail label="Created" value={task.created_date?.slice(0, 10) || "—"} />
              <Detail label="Last updated" value={task.last_updated?.slice(0, 10) || "—"} />
            </div>

            {task.google_doc_link && (
              <a href={task.google_doc_link} target="_blank" rel="noreferrer" data-testid="drawer-gdoc-link" className="inline-flex items-center gap-1.5 text-sm text-navy hover:underline">
                Google Doc <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {task.related_doc_link && (
              <a href={task.related_doc_link} target="_blank" rel="noreferrer" className="ml-4 inline-flex items-center gap-1.5 text-sm text-navy hover:underline">
                Related doc <ExternalLink className="w-3 h-3" />
              </a>
            )}

            {task.notes && (
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-1.5">Notes</div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-md p-3">{task.notes}</p>
              </div>
            )}

            {/* Activity log */}
            {isAdmin && (
              <div className="border-t border-gray-100 pt-5">
                <button onClick={loadLogs} data-testid="drawer-activity-toggle" className="text-xs font-medium uppercase tracking-wider text-navy inline-flex items-center gap-2 hover:underline">
                  <History className="w-3.5 h-3.5" /> {showLogs ? "Activity log" : "Show activity log"}
                </button>
                {showLogs && (
                  <ul className="mt-3 space-y-2.5">
                    {logs.length === 0 && <li className="text-xs text-gray-400 italic">No activity yet.</li>}
                    {logs.map(l => (
                      <li key={l.id} className="text-xs flex gap-3 items-start">
                        <span className="text-gray-400 font-mono whitespace-nowrap">{l.timestamp.slice(0, 16).replace("T", " ")}</span>
                        <span className="text-gray-700 leading-snug">
                          <span className="font-medium text-gray-900">{l.user_name}</span> {l.action}
                          {l.details && <span className="text-gray-500"> — {l.details}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {/* Footer actions */}
        <div className="border-t border-gray-100 px-6 py-3 flex items-center justify-between bg-white">
          {isAdmin ? (
            <button onClick={remove} data-testid="drawer-delete-btn" className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1.5">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          ) : <span />}
          <button onClick={onEdit} data-testid="drawer-edit-btn" className="bg-navy hover:bg-navy-800 text-white px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2">
            <Pencil className="w-3.5 h-3.5" /> Edit task
          </button>
        </div>
      </aside>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-1">{label}</div>
      <div className="text-sm text-gray-900">{value}</div>
    </div>
  );
}
